// ProjectWorkloadByAssigneeReport.jsx

import React, { useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import {
  buildProjectWorkflowReportIndex,
  buildWorkloadRows,
  PROJECT_WORKLOAD_SORT,
  UNASSIGNED_ASSIGNEE_KEY,
} from 'collection/reports/project/projectWorkflowReportIndex';

// CSS
import './ProjectWorkflowReportShared.css';

// Components
import ProfilePhoto from 'components/profilephoto/ProfilePhoto';
import SelectInput from 'components/selectinput/SelectInput';

/**
 * ProjectWorkloadByAssigneeReport
 *
 * Overview widget: open / overdue workflow steps per assignee.
 */
const ProjectWorkloadByAssigneeReport = ({
  nodes = [],
  isLoading = false,
  title = 'Workload',
  description = 'Open playbook steps by assignee across projects.',
}) => {
  const { theme } = useTheme();
  const [templateKey, setTemplateKey] = useState('');

  const index = useMemo(
    () => buildProjectWorkflowReportIndex({ nodes }),
    [nodes],
  );

  const rows = useMemo(() => (
    buildWorkloadRows(index.openSteps, {
      sortKey: PROJECT_WORKLOAD_SORT.OVERDUE,
      sortDirection: 'desc',
      templateKey,
    })
  ), [index.openSteps, templateKey]);

  const templateOptions = useMemo(() => ([
    { label: 'All playbooks', value: '' },
    ...index.templateOptions,
  ]), [index.templateOptions]);

  const maxOpen = rows.reduce((max, row) => Math.max(max, row.openCount), 0);

  return (
    <div className="project-workflow-report">
      <div className="project-workflow-report-intro">
        <div
          className="project-workflow-report-title"
          style={{ color: theme.foreground }}
        >
          {title}
        </div>
        <div
          className="project-workflow-report-description"
          style={{ color: theme.foregroundMuted }}
        >
          {isLoading
            ? description
            : rows.length
              ? `${rows.length} ${rows.length === 1 ? 'assignee' : 'assignees'} with open work`
              : description}
        </div>
      </div>

      <div className="project-workflow-report-toolbar">
        <div className="project-workflow-report-toolbar-group">
          <div
            className="project-workflow-report-filter-label"
            style={{ color: theme.foregroundMuted }}
          >
            Playbook
          </div>
          <div className="project-workflow-report-filter-select">
            <SelectInput
              height="44px"
              options={templateOptions}
              showIcons={false}
              value={templateKey}
              width="100%"
              onChange={(event) => setTemplateKey(event?.target?.value || '')}
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div
          className="project-workflow-report-empty"
          style={{ color: theme.foregroundMuted }}
        >
          Loading…
        </div>
      ) : rows.length === 0 ? (
        <div
          className="project-workflow-report-empty"
          style={{ color: theme.foregroundMuted }}
        >
          No open assigned playbook steps.
        </div>
      ) : (
        <div className="project-workflow-report-table">
          <div className="project-workflow-report-workload-row is-header">
            <div className="project-workflow-report-cell is-muted">Assignee</div>
            <div className="project-workflow-report-cell is-muted">Open</div>
            <div className="project-workflow-report-cell is-muted">Overdue</div>
            <div className="project-workflow-report-cell is-muted">Projects</div>
          </div>

          {rows.map((row) => (
            <div
              key={row.key}
              className="project-workflow-report-workload-row"
            >
              <div className="project-workflow-report-cell">
                <div className="project-workflow-report-assignee-row">
                  {row.key !== UNASSIGNED_ASSIGNEE_KEY ? (
                    <ProfilePhoto
                      borderWidth={0}
                      node={{
                        coverUrl: row.coverUrl,
                        key: row.key,
                        name: row.name,
                        userKey: row.key,
                      }}
                      size={28}
                    />
                  ) : null}
                  <span className="project-workflow-report-assignee-name">
                    {row.name}
                  </span>
                </div>
                {maxOpen > 0 ? (
                  <div
                    aria-hidden="true"
                    style={{
                      background: theme.outlineMuted,
                      borderRadius: 999,
                      height: 4,
                      marginTop: 8,
                      overflow: 'hidden',
                      width: '100%',
                    }}
                  >
                    <div
                      style={{
                        background: row.overdueCount > 0 ? theme.warning : theme.success,
                        height: '100%',
                        width: `${Math.round((row.openCount / maxOpen) * 100)}%`,
                      }}
                    />
                  </div>
                ) : null}
              </div>
              <div className="project-workflow-report-cell is-strong">
                {row.openCount}
              </div>
              <div
                className={`project-workflow-report-cell${row.overdueCount ? ' is-overdue' : ' is-muted'}`}
              >
                {row.overdueCount}
              </div>
              <div className="project-workflow-report-cell is-muted">
                {row.projectCount}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

ProjectWorkloadByAssigneeReport.displayName = 'ProjectWorkloadByAssigneeReport';

export default ProjectWorkloadByAssigneeReport;
